/**
 * Anchored Rows Store
 * 
 * Chapter-level, IndexedDB-backed store for authoritative alignment rows.
 * This is the permanent registry of confirmed alignments, separate from matrix sessions.
 */

import { create } from 'zustand';
import { dbStorage } from '../lib/indexedDBStorage';
import {
    AnchoredRow,
    AnchoredRowRecord,
    UnitMapping,
    UpsertRowOptions,
    serializeAnchoredRow,
    deserializeAnchoredRow,
    getRowLabel
} from '../types/anchoredRowTypes';
import { resolveLedgerLabelPrefix } from '../lib/contextLabelPrefix';

const DEFAULT_PREFIX = 'Cx';

function nowIso(): string {
    return new Date().toISOString();
}

function generateId(): string {
    return crypto.randomUUID();
}

/**
 * Sort rows by position
 */
function sortRows(rows: AnchoredRow[]): AnchoredRow[] {
    return [...rows].sort((a, b) => a.position - b.position);
}

/**
 * Relabel rows with sequential numbers
 */
function relabelRows(rows: AnchoredRow[], prefix: string): AnchoredRow[] {
    // NOTE: We keep `position` unchanged. `position` is the sort key and may be
    // sparse (e.g. derived from matrix row indices). Relabeling should only
    // regenerate sequential label numbers.
    return rows.map((row, index) => ({
        ...row,
        labelPrefix: prefix,
        labelNumber: index + 1
    }));
}

function samePersistedRowContent(left: AnchoredRow, right: AnchoredRow): boolean {
    return left.id === right.id
        && left.chapterId === right.chapterId
        && left.labelPrefix === right.labelPrefix
        && left.labelNumber === right.labelNumber
        && left.position === right.position
        && left.createdAt === right.createdAt
        && left.createdBy === right.createdBy
        && left.status === right.status
        && JSON.stringify(left.unitMappings) === JSON.stringify(right.unitMappings)
        && JSON.stringify(left.metadata) === JSON.stringify(right.metadata);
}

function sameDurableRowContent(left: AnchoredRow, right: AnchoredRow): boolean {
    // labelNumber is derived from position ordering whenever a chapter loads.
    // A neighboring insertion or deletion must therefore not turn every
    // following display-number change into a durable record rewrite.
    return samePersistedRowContent(left, {
        ...right,
        labelNumber: left.labelNumber
    });
}

// ============================================================================
// Store Interface
// ============================================================================

interface AnchoredRowsStoreState {
    chapterRows: Map<string, AnchoredRow[]>;
    loadingChapters: Set<string>;

    // Queries
    getRowsForChapter: (chapterId: string) => AnchoredRow[];
    findRowContainingUnit: (unitUuid: string) => AnchoredRow | null;

    // Load
    loadChapterRows: (chapterId: string) => Promise<AnchoredRow[]>;

    // CRUD
    saveRow: (row: AnchoredRow) => Promise<void>;
    upsertRows: (chapterId: string, rows: AnchoredRow[], options?: UpsertRowOptions) => Promise<void>;
    replaceResolvedRows: (
        chapterId: string,
        previousRows: AnchoredRow[],
        nextRows: AnchoredRow[],
        options?: UpsertRowOptions
    ) => Promise<void>;
    deleteRow: (rowId: string, chapterId: string) => Promise<void>;

    // Unit boundary change handlers
    updateUnitMapping: (rowId: string, witnessId: string, newUnitUuid: string, textSnapshot?: string) => Promise<void>;
    removeUnitFromRow: (rowId: string, witnessId: string) => Promise<void>;
    handleUnitMerge: (chapterId: string, oldUuids: string[], newUuid: string, newText?: string) => Promise<void>;
    handleUnitSplit: (chapterId: string, oldUuid: string, newUuids: string[]) => Promise<void>;

    // Bulk operations
    clearChapter: (chapterId: string) => Promise<void>;
    regenerateLabels: (chapterId: string, prefix?: string) => Promise<void>;
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useAnchoredRowsStore = create<AnchoredRowsStoreState>((set, get) => ({
    chapterRows: new Map(),
    loadingChapters: new Set(),

    // ============================================================================
    // Queries
    // ============================================================================

    getRowsForChapter: (chapterId) => {
        return get().chapterRows.get(chapterId) || [];
    },

    findRowContainingUnit: (unitUuid) => {
        const allRowsArrays = Array.from(get().chapterRows.values());
        for (const rows of allRowsArrays) {
            const found = rows.find(row =>
                row.unitMappings.some(m => !m.excluded && m.unitUuid === unitUuid)
            );
            if (found) return found;
        }
        return null;
    },

    // ============================================================================
    // Load
    // ============================================================================

    loadChapterRows: async (chapterId) => {
        const cached = get().chapterRows.get(chapterId);
        if (cached) return cached;

        if (get().loadingChapters.has(chapterId)) {
            // Wait for concurrent load
            return new Promise<AnchoredRow[]>((resolve) => {
                const check = setInterval(() => {
                    const rows = get().chapterRows.get(chapterId);
                    if (rows || !get().loadingChapters.has(chapterId)) {
                        clearInterval(check);
                        resolve(rows || []);
                    }
                }, 50);
            });
        }

        set(state => ({
            loadingChapters: new Set(Array.from(state.loadingChapters).concat(chapterId))
        }));

        try {
            await dbStorage.ready();
            const [records, chapter] = await Promise.all([
                dbStorage.getAllByIndex('anchored_rows', 'chapterId', chapterId) as Promise<AnchoredRowRecord[]>,
                dbStorage.get('chapters', chapterId)
            ]);
            const storedRows = sortRows(records.map(deserializeAnchoredRow));
            const rows = storedRows.length > 0
                ? relabelRows(
                    storedRows,
                    resolveLedgerLabelPrefix({
                        contextLabelPrefix: chapter?.label_prefix,
                        secondaryPrefix: storedRows[0]?.labelPrefix,
                        fallbackPrefix: DEFAULT_PREFIX
                    })
                )
                : storedRows;

            set(state => {
                const next = new Map(state.chapterRows);
                next.set(chapterId, rows);
                const loading = new Set(state.loadingChapters);
                loading.delete(chapterId);
                return { chapterRows: next, loadingChapters: loading };
            });

            return rows;
        } catch (error) {
            console.error(`Failed to load anchored rows for chapter ${chapterId}:`, error);
            set(state => {
                const loading = new Set(state.loadingChapters);
                loading.delete(chapterId);
                return { loadingChapters: loading };
            });
            return [];
        }
    },

    // ============================================================================
    // CRUD
    // ============================================================================

    saveRow: async (row) => {
        await dbStorage.ready();
        const record = serializeAnchoredRow({
            ...row,
            updatedAt: nowIso()
        });
        await dbStorage.put('anchored_rows', record);

        set(state => {
            const chapterId = row.chapterId;
            const existing = state.chapterRows.get(chapterId) || [];
            const idx = existing.findIndex(r => r.id === row.id);
            const updatedRows = idx >= 0
                ? existing.map(r => r.id === row.id ? row : r)
                : [...existing, row];

            const next = new Map(state.chapterRows);
            next.set(chapterId, sortRows(updatedRows));
            return { chapterRows: next };
        });
    },

    upsertRows: async (chapterId, rows, options = {}) => {
        await dbStorage.ready();
        const now = nowIso();
        const existingRows = get().chapterRows.get(chapterId) || [];
        const existingIds = new Set(existingRows.map(r => r.id));
        const existingById = new Map(existingRows.map(row => [row.id, row]));
        const changedRowIds = options.changedRowIds
            ? new Set(options.changedRowIds)
            : null;

        if (changedRowIds) {
            if (rows.some(row => !row.id.trim())) {
                throw new Error('Changed-row persistence requires stable incoming row identities');
            }
            const incomingIds = new Set(rows.map(row => row.id));
            changedRowIds.forEach(rowId => {
                if (!incomingIds.has(rowId)) {
                    throw new Error(`Changed row ${rowId} is missing from the anchored-row upsert`);
                }
            });
            rows.forEach(row => {
                if (changedRowIds.has(row.id)) {
                    return;
                }
                const existing = existingById.get(row.id);
                if (!existing) {
                    throw new Error(`New row ${row.id} is missing from the changed-row persistence scope`);
                }
                if (!samePersistedRowContent(existing, row)) {
                    throw new Error(`Row ${row.id} changed outside the anchored-row persistence scope`);
                }
            });
        }

        // Merge: update existing, add new
        const incoming = rows.map(row => {
            const existing = existingById.get(row.id);
            if (existing && changedRowIds && !changedRowIds.has(row.id)) {
                return existing;
            }
            return existingIds.has(row.id)
                ? { ...row, updatedAt: now }
                : {
                ...row,
                id: row.id || generateId(),
                createdAt: row.createdAt || now,
                updatedAt: now
                };
        });
        const incomingById = new Map(incoming.map(row => [row.id, row]));
        const updatedRows = [
            ...existingRows.map(row => incomingById.get(row.id) || row),
            ...incoming.filter(row => !existingIds.has(row.id))
        ];

        const preRelabelById = new Map(updatedRows.map(row => [row.id, row]));

        // Optionally relabel
        const relabeledRows = options.autoRelabel
            ? relabelRows(
                sortRows(updatedRows),
                resolveLedgerLabelPrefix({
                    contextLabelPrefix: options.labelPrefix,
                    secondaryPrefix: updatedRows[0]?.labelPrefix,
                    fallbackPrefix: DEFAULT_PREFIX
                })
            )
            : sortRows(updatedRows);
        const finalRows = relabeledRows.map(row => {
            const before = preRelabelById.get(row.id);
            const labelChanged = before
                && (
                    before.labelPrefix !== row.labelPrefix
                    || before.labelNumber !== row.labelNumber
                );
            const isDurableChange = changedRowIds
                ? changedRowIds.has(row.id)
                : incomingById.has(row.id) || Boolean(labelChanged);
            return isDurableChange && labelChanged && row.updatedAt !== now
                ? { ...row, updatedAt: now }
                : row;
        });

        // A scoped edit persists only its declared durable row changes. Row
        // numbers are presentation derived from stable position ordering, so
        // inserting one row must not rewrite every following row merely
        // because its displayed number moved. Unscoped maintenance upserts
        // retain the legacy behavior and may normalize stored labels.
        const changedRows = finalRows.filter(row => {
            if (changedRowIds) {
                return changedRowIds.has(row.id);
            }
            if (incomingById.has(row.id)) {
                return true;
            }
            const before = preRelabelById.get(row.id);
            return !before
                || before.labelPrefix !== row.labelPrefix
                || before.labelNumber !== row.labelNumber;
        });
        await dbStorage.putMany('anchored_rows', changedRows.map(serializeAnchoredRow));

        set(state => {
            const next = new Map(state.chapterRows);
            next.set(chapterId, finalRows);
            return { chapterRows: next };
        });
    },

    replaceResolvedRows: async (chapterId, previousRows, nextRows, options = {}) => {
        await dbStorage.ready();
        const now = nowIso();
        const existingRows = get().chapterRows.get(chapterId) || await get().loadChapterRows(chapterId);
        const existingById = new Map(existingRows.map(row => [row.id, row]));
        const previousRowIds = new Set(previousRows.map(row => row.id));
        const preservedRows = existingRows.filter(row => !previousRowIds.has(row.id));

        const replacementRows = nextRows.map(row => {
            const id = row.id || generateId();
            const existing = existingById.get(id);
            return {
                ...row,
                id,
                createdAt: row.createdAt || existing?.createdAt || now,
                updatedAt: existing?.updatedAt || row.updatedAt || now
            };
        });
        const mergedRows = [...preservedRows, ...replacementRows];

        const rowsWithDerivedLabels = options.autoRelabel
            ? relabelRows(
                sortRows(mergedRows),
                resolveLedgerLabelPrefix({
                    contextLabelPrefix: options.labelPrefix,
                    secondaryPrefix: mergedRows[0]?.labelPrefix || existingRows[0]?.labelPrefix,
                    fallbackPrefix: DEFAULT_PREFIX
                })
            )
            : sortRows(mergedRows);

        const finalIds = new Set(rowsWithDerivedLabels.map(row => row.id));
        const deletedRowIds = existingRows
            .filter(row => previousRowIds.has(row.id) && !finalIds.has(row.id))
            .map(row => row.id);
        const changedRows: AnchoredRow[] = [];
        const finalRows = rowsWithDerivedLabels.map(row => {
            const existing = existingById.get(row.id);
            if (!existing || !sameDurableRowContent(existing, row)) {
                const changed = {
                    ...row,
                    createdAt: row.createdAt || existing?.createdAt || now,
                    updatedAt: now
                };
                changedRows.push(changed);
                return changed;
            }
            return {
                ...row,
                createdAt: existing.createdAt,
                updatedAt: existing.updatedAt
            };
        });

        await dbStorage.commitRecordChanges({
            puts: changedRows.length > 0
                ? { anchored_rows: changedRows.map(serializeAnchoredRow) }
                : undefined,
            deletes: deletedRowIds.length > 0
                ? { anchored_rows: deletedRowIds }
                : undefined
        });

        set(state => {
            const next = new Map(state.chapterRows);
            next.set(chapterId, finalRows);
            return { chapterRows: next };
        });
    },

    deleteRow: async (rowId, chapterId) => {
        await dbStorage.ready();
        await dbStorage.delete('anchored_rows', rowId);

        set(state => {
            const existing = state.chapterRows.get(chapterId) || [];
            const filtered = existing.filter(r => r.id !== rowId);
            const next = new Map(state.chapterRows);
            next.set(chapterId, filtered);
            return { chapterRows: next };
        });
    },

    // ============================================================================
    // Unit Boundary Change Handlers
    // ============================================================================

    updateUnitMapping: async (rowId, witnessId, newUnitUuid, textSnapshot) => {
        const allRows = Array.from(get().chapterRows.values()).flat();
        const row = allRows.find(r => r.id === rowId);
        if (!row) return;

        const updatedMappings = row.unitMappings.map((m: UnitMapping) =>
            m.witnessId === witnessId
                ? { ...m, unitUuid: newUnitUuid, textSnapshot: textSnapshot ?? m.textSnapshot }
                : m
        );

        await get().saveRow({
            ...row,
            unitMappings: updatedMappings,
            updatedAt: nowIso()
        });
    },

    removeUnitFromRow: async (rowId, witnessId) => {
        const allRows = Array.from(get().chapterRows.values()).flat();
        const row = allRows.find(r => r.id === rowId);
        if (!row) return;

        const updatedMappings = row.unitMappings.filter((m: UnitMapping) => m.witnessId !== witnessId);

        // If no mappings left, delete the row
        if (updatedMappings.length === 0) {
            await get().deleteRow(rowId, row.chapterId);
        } else {
            await get().saveRow({
                ...row,
                unitMappings: updatedMappings,
                updatedAt: nowIso()
            });
        }
    },

    handleUnitMerge: async (chapterId, oldUuids, newUuid, newText) => {
        const rows = get().chapterRows.get(chapterId) || [];
        const affectedRowIds = rows
            .filter(row => row.unitMappings.some(m => oldUuids.includes(m.unitUuid)))
            .map(row => row.id);
        if (affectedRowIds.length > 0) {
            const affectedRowIdSet = new Set(affectedRowIds);
            const updatedRows = rows.map(row => affectedRowIdSet.has(row.id)
                ? {
                    ...row,
                    unitMappings: row.unitMappings.map(m =>
                        oldUuids.includes(m.unitUuid)
                            ? { ...m, unitUuid: newUuid, textSnapshot: newText ?? m.textSnapshot }
                            : m
                    )
                }
                : row
            );
            await get().upsertRows(chapterId, updatedRows, {
                changedRowIds: affectedRowIds
            });
        }

        console.log(`[AnchoredRowsStore] handleUnitMerge: Updated ${affectedRowIds.length} rows`);
    },

    handleUnitSplit: async (chapterId, oldUuid, newUuids) => {
        const rows = get().chapterRows.get(chapterId) || [];
        const affectedRowIds = rows
            .filter(row => row.unitMappings.some(m => m.unitUuid === oldUuid))
            .map(row => row.id);
        if (affectedRowIds.length > 0) {
            const affectedRowIdSet = new Set(affectedRowIds);
            const updatedRows = rows.map(row => affectedRowIdSet.has(row.id)
                ? {
                    ...row,
                    unitMappings: row.unitMappings.map(m =>
                        m.unitUuid === oldUuid
                            ? { ...m, unitUuid: newUuids[0] }
                            : m
                    ),
                    status: 'needs-review' as const
                }
                : row
            );
            await get().upsertRows(chapterId, updatedRows, {
                changedRowIds: affectedRowIds
            });
        }

        console.log(`[AnchoredRowsStore] handleUnitSplit: Marked ${affectedRowIds.length} rows for review`);
    },

    // ============================================================================
    // Bulk Operations
    // ============================================================================

    clearChapter: async (chapterId) => {
        const rows = get().chapterRows.get(chapterId) || [];
        await dbStorage.ready();
        await dbStorage.commitRecordChanges({
            deletes: rows.length > 0
                ? { anchored_rows: rows.map(row => row.id) }
                : undefined
        });

        set(state => {
            const next = new Map(state.chapterRows);
            next.delete(chapterId);
            return { chapterRows: next };
        });
    },

    regenerateLabels: async (chapterId, prefix) => {
        const rows = get().chapterRows.get(chapterId) || [];
        if (rows.length === 0) return;

        const effectivePrefix = resolveLedgerLabelPrefix({
            contextLabelPrefix: prefix,
            secondaryPrefix: rows[0]?.labelPrefix,
            fallbackPrefix: DEFAULT_PREFIX
        });
        const relabelled = relabelRows(sortRows(rows), effectivePrefix);

        // Both prefix and sequential number are chapter-level projections.
        // Persist one chapter prefix, never one rewrite per anchored row.
        if (prefix) {
            const chapter = await dbStorage.get('chapters', chapterId);
            if (chapter && chapter.label_prefix !== effectivePrefix) {
                await dbStorage.put('chapters', {
                    ...chapter,
                    label_prefix: effectivePrefix
                });
            }
        }

        set(state => {
            const next = new Map(state.chapterRows);
            next.set(chapterId, relabelled);
            return { chapterRows: next };
        });
    }
}));

// Re-export types and helpers
export { getRowLabel };
export type { AnchoredRow, UnitMapping };
